import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link as LinkIcon, Unlink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import InfoPopover from '@/components/common/InfoPopover';
import { useAuth } from '@/contexts/AuthContext';
import { authAPI } from '@/services/api';
import { useQuery } from '@tanstack/react-query';

// Google og Facebook logoer som SVG
const GoogleLogo = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const FacebookLogo = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24">
    <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

interface OAuthAccountLinkingProps {
  onMessage?: (message: { type: 'success' | 'error'; text: string }) => void;
}

export const OAuthAccountLinking: React.FC<OAuthAccountLinkingProps> = ({ onMessage }) => {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [unlinkDialog, setUnlinkDialog] = useState<{ open: boolean; provider: 'google' | 'facebook' | null }>({
    open: false,
    provider: null,
  });

  // Hent OAuth-innstillinger for organisasjonen
  const { data: oauthSettings, isLoading: isLoadingSettings } = useQuery({
    queryKey: ['oauthSettings'],
    queryFn: async () => {
      try {
        const response = await authAPI.getOAuthSettings();
        return response.settings || { allowGoogleOAuth: true, allowFacebookOAuth: true };
      } catch (error) {
        console.error('Failed to fetch OAuth settings:', error);
        // Returner true som default hvis API-kallet feiler (sikkerhetsfall - vis komponenten)
        return { allowGoogleOAuth: true, allowFacebookOAuth: true };
      }
    },
    staleTime: 0, // Data er alltid stale, så den refetches ved hver mount
    gcTime: 5 * 60 * 1000, // Cache i 5 minutter (gcTime erstatter cacheTime i React Query v5)
    refetchOnMount: true, // Refetch når komponenten monteres
  });

  // Sjekk for success/error i URL
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    
    if (success === 'account_linked') {
      onMessage?.({ type: 'success', text: 'Kontoen ble lenket!' });
      refreshUser();
      // Fjern query params
      window.history.replaceState({}, '', '/profile');
    } else if (error === 'link_failed') {
      onMessage?.({ type: 'error', text: 'Kunne ikke lenke kontoen. Prøv igjen.' });
      // Fjern query params
      window.history.replaceState({}, '', '/profile');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Kun lytt til searchParams-endringer

  const handleLinkGoogle = () => {
    setLoading(true);
    authAPI.linkGoogle();
  };

  const handleLinkFacebook = () => {
    setLoading(true);
    authAPI.linkFacebook();
  };

  const handleUnlink = (provider: 'google' | 'facebook') => {
    setUnlinkDialog({ open: true, provider });
  };

  const confirmUnlink = async () => {
    if (!unlinkDialog.provider) return;

    try {
      setLoading(true);
      await authAPI.unlinkProvider(unlinkDialog.provider);
      onMessage?.({ 
        type: 'success', 
        text: `${unlinkDialog.provider === 'google' ? 'Google' : 'Facebook'}-konto frakoblet` 
      });
      await refreshUser();
    } catch (error: any) {
      console.error('Unlink error:', error);
      onMessage?.({ type: 'error', text: 'Kunne ikke frakoble kontoen' });
    } finally {
      setLoading(false);
      setUnlinkDialog({ open: false, provider: null });
    }
  };

  const isGoogleLinked = !!user?.googleId;
  const isFacebookLinked = !!user?.facebookId;

  // Sjekk om Google OAuth er aktivert for organisasjonen
  // Hvis settings ikke er lastet ennå (loading), vis komponenten (bruk true som fallback)
  // Når settings er lastet, bruk faktiske verdier fra API
  const isGoogleEnabled = isLoadingSettings 
    ? true // Vis mens vi laster (sikkerhetsfall)
    : (oauthSettings?.allowGoogleOAuth === true); // Eksplisitt sjekk for true
  
  // Sjekk om Facebook OAuth er aktivert for organisasjonen
  const isFacebookEnabled = isLoadingSettings
    ? true // Vis mens vi laster (sikkerhetsfall)
    : (oauthSettings?.allowFacebookOAuth === true); // Eksplisitt sjekk for true

  // Hvis settings er lastet og ingen OAuth-metoder er aktivert, vis ikke komponenten
  if (!isLoadingSettings && !isGoogleEnabled && !isFacebookEnabled) {
    return null;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LinkIcon className="h-5 w-5" />
            Koblede kontoer
            <InfoPopover label="Hva er koblede kontoer?">
              <p>
                Lister tillatte måter du kan logge inn i Voluplan på. Eksempel: logge inn med
                Google-konto.
              </p>
            </InfoPopover>
          </CardTitle>
          <CardDescription>
            Koble til Google eller Facebook for enklere innlogging
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Google */}
          {isGoogleEnabled && (
            <>
              <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-white border flex items-center justify-center">
                    <GoogleLogo />
                  </div>
                  <div>
                    <p className="font-medium">Google</p>
                    {isGoogleLinked ? (
                      <p className="text-sm text-muted-foreground">Koblet til</p>
                    ) : (
                      <p className="text-sm text-muted-foreground">Ikke koblet</p>
                    )}
                  </div>
                </div>
                <div>
                  {isGoogleLinked ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUnlink('google')}
                      disabled={loading}
                      className="gap-2"
                    >
                      <Unlink className="h-4 w-4" />
                      Frakoble
                    </Button>
                  ) : (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleLinkGoogle}
                      disabled={loading}
                      className="gap-2"
                    >
                      <LinkIcon className="h-4 w-4" />
                      Koble til
                    </Button>
                  )}
                </div>
              </div>

              {isGoogleEnabled && isFacebookEnabled && <Separator />}
            </>
          )}

          {/* Facebook */}
          {isFacebookEnabled && (
            <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-white border flex items-center justify-center">
                  <FacebookLogo />
                </div>
                <div>
                  <p className="font-medium">Facebook</p>
                  {isFacebookLinked ? (
                    <p className="text-sm text-muted-foreground">Koblet til</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">Ikke koblet</p>
                  )}
                </div>
              </div>
              <div>
                {isFacebookLinked ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleUnlink('facebook')}
                    disabled={loading}
                    className="gap-2"
                  >
                    <Unlink className="h-4 w-4" />
                    Frakoble
                  </Button>
                ) : (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleLinkFacebook}
                    disabled={loading}
                    className="gap-2"
                  >
                    <LinkIcon className="h-4 w-4" />
                    Koble til
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className="mt-4 p-3 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>Tips:</strong> Når du kobler til en konto, kan du logge inn med den i stedet for passord.
              Du kan alltid frakoble kontoen senere.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Unlink Confirmation Dialog */}
      <AlertDialog open={unlinkDialog.open} onOpenChange={(open) => !open && setUnlinkDialog({ open: false, provider: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Frakoble {unlinkDialog.provider === 'google' ? 'Google' : 'Facebook'}?</AlertDialogTitle>
            <AlertDialogDescription>
              Er du sikker på at du vil frakoble {unlinkDialog.provider === 'google' ? 'Google' : 'Facebook'}-kontoen din?
              Du vil ikke lenger kunne logge inn med denne kontoen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={confirmUnlink}>
              Frakoble
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

